import { getApiErrorDetails } from './apiError';

const ACTIONABLE_ERRORS: Record<string, string> = {
  custom_image_artifact_is_not_uploaded: 'The image artifact is not ready. Wait for upload validation to finish, then retry the build.',
  custom_product_image_has_no_usable_replica: 'This image has no ready replica. Build it successfully on an eligible Agent first.',
  custom_image_replica_is_not_ready_on_selected_agent: 'The selected image replica is not ready on this Agent. Refresh the image state or choose another ready replica.',
  custom_build_agent_is_not_active: 'The selected Agent is disabled. Enable it in Host Agents before building.',
  custom_build_agent_is_not_connected: 'The selected Agent is offline. Restore its Socket.IO connection before building.',
  custom_build_agent_is_not_opted_in: 'The selected Agent is not opted in for custom workloads. Enable Custom workloads in its settings.',
  custom_build_agent_has_no_authentication_token: 'The selected Agent has no authentication token. Rotate or configure its token first.',
  custom_instance_agent_is_not_eligible: 'The selected Agent is not active, connected, or opted in for custom workloads.',
  custom_instance_agent_has_no_valid_connection: 'The selected Agent cannot be reached by Mothership. Check its token and connection state.',
  custom_agent_capacity_or_ports_unavailable: 'The Agent has insufficient CPU, RAM, or storage capacity, or a required host port is already reserved. Choose another replica/profile or release capacity.',
  custom_instance_container_name_conflict: 'That instance name collides with an existing customer or custom container. Choose a different name.',
  custom_instance_lifecycle_operation_in_progress: 'Another lifecycle operation is still in progress. Wait for it to finish and refresh the instance.',
  custom_instance_is_not_prepared: 'The instance has not been confirmed by its Agent yet. Wait for preparation or resolve the reported Agent error.',
  custom_image_upload_parts_incomplete: 'Some ZIP parts are still missing. Resume the upload with the same file before completing it.',
  custom_image_upload_expired: 'This multipart upload expired. Create a new image version and upload the ZIP again.',
  custom_product_image_version_already_exists: 'That version already exists for this product. Versions are immutable; choose a new version.',
  custom_image_artifact_copy_in_progress: 'The ZIP is still being copied or validated. Wait for it to finish before deleting this release.',
  custom_image_artifact_is_being_copied: 'Another release is copying this ZIP. Wait for the copy to finish before deleting the source.',
  custom_image_copy_source_not_available: 'This release no longer has a verified ZIP available. Refresh and choose another source release.',
  custom_image_copy_idempotency_conflict: 'This copy request was already used with different inputs. Refresh the image list before trying again.',
  artifact_copy_expired: 'The ZIP copy did not complete within its recovery window. Delete this failed release and create a new copy.',
  custom_product_image_cannot_be_retired_in_current_state: 'Only uploaded or ready releases can be retired. Delete a failed upload instead.',
  custom_product_image_has_active_replicas: 'Agent replica cleanup is still pending for this release.',
  custom_product_image_is_used_by_instances: 'This release is pinned by a custom instance. Delete the instance before deleting the release.',
  custom_product_revision_unchanged: 'The definition is unchanged. Edit it before creating another immutable revision.',
};

export function newIdempotencyKey(scope: string): string {
  const random = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `custom-ui:${scope}:${random}`.slice(0, 127);
}

export function customErrorMessage(error: unknown, fallback: string): string {
  const details = getApiErrorDetails(error, fallback);
  const rawCode = details.code || details.message;
  const code = rawCode.split(':', 1)[0];
  return ACTIONABLE_ERRORS[code] || details.message.replaceAll('_', ' ');
}

export function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let amount = value;
  let unit = 0;
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }
  return `${amount >= 10 || unit === 0 ? amount.toFixed(0) : amount.toFixed(1)} ${units[unit]}`;
}

export function operationInProgress(phase?: string): boolean {
  return Boolean(phase && !['succeeded', 'failed_terminal'].includes(phase));
}
